import React from 'react';
import { ChatHeader } from './ChatHeader';
import { MessageList } from './MessageList';
import { useChat } from '../../hooks';
import ReportMessageModal from './ReportMessageModal';

import { Conversation, Message } from '../../types';
import { remoteConfig } from '../../../../utility/firebaseRemoteConfigHelper';

import { messageService } from '../../api/messageService';
import { MessageInput } from './MessageInput';

interface ChatWindowProps {
  conversation: Conversation | null;
  currentUserId: string;
  onBack?: () => void; // For mobile
}

export const ChatWindow: React.FC<ChatWindowProps> = ({ conversation, currentUserId, onBack }) => {
  const {
    messages,
    loading,
    loadingMore,
    sending,
    hasMore,
    sendMessage,
    sendAttachment,
    loadMoreMessages,
  } = useChat(conversation);
  const attachmentsEnabled = remoteConfig.isAttachmentEnabledInChat();

  // Report message modal state
  const [reportModalOpen, setReportModalOpen] = React.useState(false);
  const [messageToReport, setMessageToReport] = React.useState<Message | null>(null);

  const handleReportMessage = (message: Message) => {
    setMessageToReport(message);
    setReportModalOpen(true);
  };

  // Merge messages with initial inquiry if needed (Moved before conditional return)
  const displayMessages = React.useMemo(() => {
    if (
      conversation?.metadata?.type === 'inquiry' &&
      conversation.lastMessage &&
      messages.length === 0
    ) {
      const inquiryMessage: Message = {
        id: `inquiry-${conversation.id}`,
        senderId: conversation.lastMessage.senderId,
        content: conversation.lastMessage.content,
        timestamp: conversation.lastMessage.timestamp,
        type: 'text',
        status: 'read',
      };
      return [inquiryMessage, ...messages];
    }
    return messages;
  }, [messages, conversation]);

  // Mark as read when conversation is opened
  React.useEffect(() => {
    if (conversation?.id && currentUserId) {
      messageService.markAsRead(conversation.id, currentUserId);
    }
  }, [conversation?.id, currentUserId]);

  // Determine display info
  let displayName = 'Conversation';
  let photoUrl: string | null = null;
  let isGuest = false;

  if (conversation) {
    const otherParticipantId =
      conversation.participantIds.find((id) => id !== currentUserId) || 'Unknown';

    if (conversation.metadata?.type === 'inquiry' && conversation.metadata.guestName) {
      displayName = conversation.metadata.guestName;
      isGuest = true;
    } else if (conversation.participants && conversation.participants[otherParticipantId]) {
      const p = conversation.participants[otherParticipantId];
      displayName = p.displayName;
      photoUrl = p.photoUrl;
    } else {
      displayName = `User ${otherParticipantId.slice(0, 5)}`;
    }
  }

  if (!conversation) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-400 bg-gray-50">
        <svg
          className="w-16 h-16 mb-4 opacity-50"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        <p>Select a conversation to start messaging</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full bg-white overflow-hidden">
      <ChatHeader
        title={displayName}
        photoUrl={photoUrl}
        isGuest={isGuest}
        isOnline={false} // TODO: Real online status
        onBack={onBack}
      />
      <MessageList
        messages={displayMessages}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        onLoadMore={loadMoreMessages}
        currentUserId={currentUserId}
        onReportMessage={handleReportMessage}
      />
      {isGuest && conversation?.metadata?.guestEmail ? (
        <div className="p-4 bg-white border-t border-gray-200">
          <a
            href={`mailto:${conversation.metadata.guestEmail}`}
            className="no-underline flex items-center justify-center w-full gap-2 px-4 py-3 font-semibold text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
            Reply via Email
          </a>
          <p className="mt-2 text-xs text-center text-gray-500">
            Guest inquiries require email communication as they don't have a RentSmart account.
          </p>
        </div>
      ) : (
        <MessageInput
          onSendMessage={(content: string) => sendMessage(currentUserId, content)}
          onSendAttachment={(file: File) => sendAttachment(currentUserId, file)}
          disabled={sending}
          attachmentsEnabled={attachmentsEnabled}
        />
      )}

      {/* Report Message Modal */}
      <ReportMessageModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        message={messageToReport}
        conversationId={conversation?.id || ''}
        currentUserId={currentUserId}
      />
    </div>
  );
};
